import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAnglesLeft, faPlus, faEdit } from '@fortawesome/free-solid-svg-icons';
import Tabs from '@frontend/Components/Tabs';
import ClientService from '@frontend/AppSettings/services/ClientService';
import AgentsService from '@frontend/AppSettings/services/AgentsService';
import { isEngineeringEnvironment } from '@frontend/AppSettings/environmentConfig';
import './TopBar.css';

interface StakeholderDetailsTopBarProps {
  setActivePage: (page: string) => void;
  activePage?: string;
  stakeholderId: string;
  type: 'individual' | 'organization';
  onOpenCreateTaskModal?: () => void;
  onOpenEditModal?: () => void;
  onStakeholderDataChange?: (data: any) => void;
}

const StakeholderDetailsTopBar: React.FC<StakeholderDetailsTopBarProps> = ({
  setActivePage,
  activePage = 'summary',
  stakeholderId,
  type,
  onOpenCreateTaskModal,
  onOpenEditModal,
  onStakeholderDataChange,
}) => {
  const navigate = useNavigate();
  const [stakeholderData, setStakeholderData] = useState<any>(null);

  useEffect(() => {
    const fetchStakeholderData = async () => {
      try {
        let data;
        if (type === 'individual') {
          // Try to fetch as client first, then as agent
          try {
            const clients = await ClientService.getClients();
            data = clients.find((c: any) => String(c.ClientID) === stakeholderId);
            if (!data) {
              const agents = await AgentsService.getAgents();
              data = agents.find((a: any) => String(a.AgentID) === stakeholderId);
            }
          } catch (error) {
            console.error('Error fetching stakeholder:', error);
          }
        } else {
          // For organizations, the stakeholderId is in format "org-{ClientID}" or "org-{AgentID}"
          // Extract the actual ID and find the organization
          const actualId = stakeholderId.startsWith('org-') 
            ? stakeholderId.replace('org-', '') 
            : stakeholderId;
          
          const clients = await ClientService.getClients();
          const agents = await AgentsService.getAgents();
          
          // Try to find by ClientID first
          let foundClient = clients.find((c: any) => String(c.ClientID) === actualId);
          if (foundClient && foundClient.Company) {
            // Found a client with this ID, use it as the base
            data = foundClient;
          } else {
            // Try to find by AgentID
            let foundAgent = agents.find((a: any) => String(a.AgentID) === actualId);
            if (foundAgent && foundAgent.Company) {
              // Found an agent with this ID, use it as the base
              data = foundAgent;
            } else {
              // Fallback: try to find by Company name directly
              data = clients.find((c: any) => c.Company === stakeholderId || c.Company === actualId);
            }
          }
        }
        setStakeholderData(data);
        if (onStakeholderDataChange) {
          onStakeholderDataChange(data);
        }
      } catch (error) {
        console.error('Error fetching stakeholder data:', error);
      }
    };

    fetchStakeholderData();
  }, [stakeholderId, type, onStakeholderDataChange]);

  const stakeholderName = stakeholderData
    ? type === 'organization'
      ? stakeholderData.Company || 'Unknown Organization'
      : `${stakeholderData.FirstName || ''} ${stakeholderData.LastName || ''}`.trim() || 'Unknown Individual'
    : 'Loading...';

  const stakeholderSubtitle = stakeholderData
    ? type === 'organization'
      ? stakeholderData.Status || 'Organization'
      : stakeholderData.Title || stakeholderData.Jurisdiction || ''
    : '';
  // Show company name for individuals, hide for organizations (they are the company)
  const stakeholderCompany = type === 'individual' 
    ? (stakeholderData?.Company || null)
    : null;

  const switchToSection = (sectionName: string) => {
    setActivePage(sectionName);
  };

  const entityLabelPlural = isEngineeringEnvironment ? 'Projects' : 'Matters';
  const tabs = [
    { id: 'summary', label: 'Summary & Details', onClick: () => switchToSection('summary') },
    { id: 'matters', label: entityLabelPlural, onClick: () => switchToSection('matters') },
    { id: 'actionItems', label: 'Action Items', onClick: () => switchToSection('actionItems') },
    ...(isEngineeringEnvironment ? [] : [{ id: 'financials', label: 'Financials', onClick: () => switchToSection('financials') }]),
  ];

  const handleTabChange = (tabId: string) => {
    const match = tabs.find((t) => t.id === tabId);
    if (match) {
      match.onClick();
    }
  };

  return (
    <>
      <div className="stakeholder-topbar">
        <div className="stakeholder-topbar-left">
          <button
            className="stakeholder-topbar-back-button"
            onClick={() => navigate('/sept-orbit')}
            aria-label="Back to SEPT Orbit"
          >
            <FontAwesomeIcon icon={faAnglesLeft} />
          </button>
          <div className="stakeholder-topbar-title">
            <div className="stakeholder-topbar-name-row">
              <div className="stakeholder-topbar-name">{stakeholderName}</div>
              {stakeholderCompany && (
                <div className="stakeholder-topbar-health">{stakeholderCompany}</div>
              )}
            </div>
            <div className="stakeholder-topbar-subtitle-row">
              {stakeholderSubtitle && (
                <div className="stakeholder-topbar-subtitle">{stakeholderSubtitle}</div>
              )}
            </div>
          </div>
        </div>
        <div className="stakeholder-topbar-right">
          <button
            className="stakeholder-topbar-action-button stakeholder-topbar-action-secondary"
            onClick={() => {
              if (onOpenEditModal) {
                onOpenEditModal();
              }
            }}
          >
            <FontAwesomeIcon icon={faEdit} />
            Edit
          </button>
          <button
            className="stakeholder-topbar-action-button stakeholder-topbar-action-secondary"
            onClick={() => {
              if (onOpenCreateTaskModal) {
                onOpenCreateTaskModal();
              } else {
                navigate('/createTasks');
              }
            }}
          >
            <FontAwesomeIcon icon={faPlus} />
            Action Item
          </button>
          <button
            className="stakeholder-topbar-action-button stakeholder-topbar-action-secondary"
            onClick={() => navigate(isEngineeringEnvironment ? '/createProjects' : '/createMatters')}
          >
            <FontAwesomeIcon icon={faPlus} />
            {isEngineeringEnvironment ? 'Project' : 'Matter'}
          </button>
          {!isEngineeringEnvironment && (
            <button
              className="stakeholder-topbar-action-button stakeholder-topbar-action-primary"
              onClick={() => {
                // TODO: Implement invoice
                console.log('Invoice clicked');
              }}
            >
              <FontAwesomeIcon icon={faPlus} />
              Invoice
            </button>
          )}
        </div>
      </div>
      <div className="stakeholder-tabs">
        <Tabs
          tabs={tabs.map(({ id, label }) => ({ id, label }))}
          activeTabId={activePage}
          onTabChange={handleTabChange}
          variant="underline"
        />
      </div>
    </>
  );
};

export default StakeholderDetailsTopBar;

